/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Select, { SelectPresentationProps, SelectProps } from "@eisinc/MobiusOverrides/Select";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    state: state.components.contactUsForm.fieldValues["state"],
    selectedCountry: state.components.contactUsForm.fieldValues["country"],
    countries: getCurrentCountries(state),
    isCountryLoadNeeded: !getCurrentCountries(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface StateFieldStyles {
    stateSelectField?: SelectPresentationProps;
}

export const stateFieldStyles: StateFieldStyles = {
    stateSelectField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = stateFieldStyles;

const StateField: React.FC<Props> = ({
    fields,
    state,
    selectedCountry,
    countries,
    isCountryLoadNeeded,
    shellContext,
    setFieldValue,
    loadCurrentCountries,
}) => {
    React.useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [stateErrorMessage, setStateErrorMessage] = React.useState("");
    const [states, setStates] = React.useState<StateModel[]>([]);

    React.useEffect(() => {
        let country = {} as any;
        if (selectedCountry && countries) {
            country = countries?.filter(co => co.abbreviation === selectedCountry);
        }
        setStates(country[0]?.states || []);
        // set the state to null on each country change and remove the validation message
        setStateErrorMessage("");
    }, [selectedCountry, !isCountryLoadNeeded]);

    React.useEffect(() => {
        contactFormContext.validators.state = validateState;
        if (typeof state !== "undefined") {
            validateState();
        }
    }, [state]);

    React.useEffect(() => {
        contactFormContext.validators.state = validateState;
    }, [isRequired]);

    const validateState = () => {
        const errorMessage = isRequired && !state ? (siteMessage("Field_Required", label) as string) : "";
        setStateErrorMessage(errorMessage);
        return !errorMessage;
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const country = countries?.filter(co => co.abbreviation === selectedCountry);
        const state =
            country && country[0] ? country[0].states?.find(s => s.abbreviation === event.target.value) : undefined;
        setFieldValue({ key: "state", value: state?.abbreviation || "" });
    };

    return (
        <Select
            name="state"
            {...styles.stateSelectField}
            label={label}
            value={state}
            required={isRequired}
            onChange={handleStateChange}
            error={stateErrorMessage}
            disabled={!states.length}
        >
            <option value="">{hintText}</option>
            {states.map(st => (
                <option key={st.id} value={st.abbreviation}>
                    {st.name}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(StateField)),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select State",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select State",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
